import React, { useState, useEffect, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Input, Button } from 'reactstrap';
import { Description, FlexRow, Notification, StaticCol } from '../../../../styledComponents/styledComponents';
import {
   hashSelector,
   cancelTokenSelector,
   troubleEndSelector,
   troubleStartSelector,
   troubleIngestInputSelector,
   troubleIngestStreamSelector,
   troubleSyncSettingsSelector,
   troublePlayoutStreamSelector,
} from '../../../../redux/selectors/selectors';

import BlockSpinner from '../../../common/BlockSpinner/BlockSpinner';

import AlertsTable from './AlertTable';

import {
   setTroubleshootingIngestInput,
   resetTroubleshooting,
   setTroubleshootingIngestStream,
   setTroubleshootingSelectedAlert,
} from '../../../../redux/actions/general';
import { useNonInitialEffect } from '../../../../hooks/useNonInitialEffect';
import { URL_PARAMS } from '../../../../urlSync/urlParamNames';
import IngestService from './IngestService';

import { setAlarm } from '../../../../redux/actions/general';
import Axios from 'axios';
import { useResetSignal } from './../general/useResetSignal';
import StreamSelection from '../StreamSelection';
import { SEARCH_TYPE } from '../constants';
import PublishesTableContainer from './PublishesTableContainer';
import { checkIfTokenRefreshIsRequired } from '../../../../misc/BrowserStorageManager';

const emptyData = { inputType: '', issueMsg: '', streamsData: {} };
const emptyStreamsData = { valid: false, info: '', created: '' };

function StreamSearch({ searchType }) {
   const dispatch = useDispatch();

   // ## React useSelector hooks
   const orgaHash = useSelector(hashSelector);
   const cancelToken = useSelector(cancelTokenSelector);

   const start = useSelector(troubleStartSelector);
   const end = useSelector(troubleEndSelector);

   const ingestInputObj = useSelector(troubleIngestInputSelector);
   const selectedStreamObj = useSelector(troubleIngestStreamSelector);

   // ## React useState hooks

   const [inputValue, setInputValue] = useState('');
   const [isStreamSelectionOpen, setOpenState] = useState(true);
   const [inputType, setInputType] = useState('');
   const [issueMsg, setIssueMsg] = useState('');
   const [streamsData, setStreamsData] = useState({});
   const [isLoading, setLoadingState] = useState(false);
   const [error, setError] = useState();
   const [url, setUrl] = useState(`/v2/troubleshooting/ingest?from=${start.format()}&to=${end.format()}`);

   const syncSettings = useSelector(troubleSyncSettingsSelector);
   const playoutStream = useSelector(troublePlayoutStreamSelector);

   const [syncNote, setSyncNote] = useState('');

   const [isMounting, setMountingState] = useState(true);

   // ## React useRef hooks

   const componenthasBeenMounted = useRef(false);
   const inputValueRef = useRef(inputValue);

   // ## Constants

   const { ingestInput } = ingestInputObj;
   const inputInputRef = useRef(ingestInput);
   inputInputRef.current = ingestInput;

   const { ingestStream: selectedStream } = selectedStreamObj;

   inputValueRef.current = inputValue;

   const { valid, info, created } =
      selectedStream !== '' && streamsData[selectedStream] !== undefined
         ? streamsData[selectedStream]
         : emptyStreamsData;

   const showInputNotification = issueMsg !== '' || error;
   const showStreamNotification = valid === false;
   const dataAvailable = Object.keys(streamsData).length > 0;
   const showStreamSelection = ingestInput !== '' && inputType === 'tag' && dataAvailable;

   const resetIsInProgress = useResetSignal(start, end, orgaHash, ingestInput, isMounting);

   // set input with store streamname (if available) at mounting
   if (isMounting) {
      if (ingestInput !== '' && inputValue !== ingestInput) {
         setInputValue(ingestInput);
      }
   }

   // ## React useEffect hooks

   useEffect(() => {
      const syncIsActive = syncSettings.syncStart !== '';
      if (syncIsActive) {
         if (ingestInput !== playoutStream) {
            setSyncNote(
               `NOTE: During synchronization the transcoded output stream '${playoutStream}' is translated to the original input stream '${ingestInput}'`,
            );
         } else {
            setSyncNote('');
         }
      }
   }, [syncSettings, playoutStream, ingestInput]);

   useEffect(() => {
      setMountingState(false);
      componenthasBeenMounted.current = true;
   }, []);

   // set input text
   useEffect(() => {
      if (ingestInput !== '' && inputValueRef.current !== ingestInput) {
         setInputValue(ingestInput);
      }
   }, [ingestInput]);

   useEffect(() => {
      if (error) {
         if (error instanceof Axios.Cancel) {
            return;
         }
         dispatch(setAlarm('danger', error));
         // set initialState
      }
   }, [error, dispatch]);

   useEffect(() => {
      // either input was stream name or searched tag has just one stream -> no user selection needed
      if (Object.keys(streamsData).length === 1) {
         const streamName = Object.keys(streamsData)[0];
         // console.log('streamName :>> ', streamName);
         if (selectedStream !== streamName && selectedStream !== inputInputRef.current) {
            dispatch(
               setTroubleshootingIngestStream(getStreamPayload(streamName), getStreamPayloadToUrlPush(streamName)),
            );
         }
         setOpenState(false);
      }
      // searched tag has more than one stream
      if (Object.keys(streamsData).length > 1 && !Object.keys(streamsData).includes(selectedStream)) {
         dispatch(setTroubleshootingIngestStream(getStreamPayload(''), getStreamPayloadToUrlPush('')));
         setOpenState(selectedStream === '');
      }
   }, [streamsData, dispatch, selectedStream]);

   // changed date / interval / orga => reset
   useNonInitialEffect(() => {
      dispatch(
         resetTroubleshooting({
            [URL_PARAMS.TROUBLE_INGEST_INPUT]: '',
            [URL_PARAMS.TROUBLE_INGEST_STREAM]: '',
            [URL_PARAMS.TROUBLE_INGEST_PUBLISH_START]: '',
            [URL_PARAMS.TROUBLE_INGEST_PUBLISH_END]: '',
            [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_START]: '',
            [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_END]: '',
            [URL_PARAMS.TROUBLE_PLAYOUT_INPUT]: '',
            [URL_PARAMS.TROUBLE_PLAYOUT_STREAM]: '',
            [URL_PARAMS.TROUBLE_PLAYOUT_ID]: '',
            [URL_PARAMS.TROUBLE_PLAYOUT_FINE_START]: '',
            [URL_PARAMS.TROUBLE_PLAYOUT_FINE_END]: '',
         }),
      );
      dispatch(
         setTroubleshootingSelectedAlert(
            {
               alertId: '',
               alertFineStart: '',
               alertFineEnd: '',
            },
            {
               [URL_PARAMS.ALERT_ID]: '',
               [URL_PARAMS.ALERT_FINE_START]: '',
               [URL_PARAMS.ALERT_FINE_END]: '',
            },
         ),
      );
      resetState();
      setUrl(`/v2/troubleshooting/ingest?from=${start.format()}&to=${end.format()}`);
   }, [start, end, orgaHash]);

   // ## Handler functions

   // press ENTER handler
   function handleKeyDown(event) {
      if (event.key === 'Enter') {
         handleSearchClick();
      }
   }

   // GO button handler
   function handleSearchClick() {
      const trimmedInputValue = inputValue.trim();

      if (trimmedInputValue !== '') {
         setInputValue(trimmedInputValue);
         resetState();
         setSyncNote('');
         dispatch(
            setTroubleshootingIngestInput(
               { ingestInput: trimmedInputValue },
               {
                  [URL_PARAMS.TROUBLE_INGEST_INPUT]: trimmedInputValue,
                  [URL_PARAMS.TROUBLE_INGEST_STREAM]: '',
                  [URL_PARAMS.TROUBLE_INGEST_PUBLISH_START]: '',
                  [URL_PARAMS.TROUBLE_INGEST_PUBLISH_END]: '',
                  [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_START]: '',
                  [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_END]: '',
               },
            ),
         );
         dispatch(
            setTroubleshootingSelectedAlert(
               {
                  alertId: '',
                  alertFineStart: '',
                  alertFineEnd: '',
               },
               {
                  [URL_PARAMS.ALERT_ID]: '',
                  [URL_PARAMS.ALERT_FINE_START]: '',
                  [URL_PARAMS.ALERT_FINE_END]: '',
               },
            ),
         );
         setUrl(`/v2/troubleshooting/ingest?from=${start.format()}&to=${end.format()}`);
         checkIfTokenRefreshIsRequired();
      }
   }

   function handleStreamNameResult(res) {
      const { data, isLoading, error } = res;
      // console.log('res f:>> ', res);
      const { inputType, issueMsg, streamsData } = data ? data : emptyData;
      setInputType(inputType);
      setIssueMsg(issueMsg);
      setStreamsData(streamsData);
      setLoadingState(isLoading);
      setError(error);
   }

   const errorMessage = error && error.message ? error.message : error;

   // Other functions
   function resetState() {
      setInputType('');
      setIssueMsg('');
      setStreamsData({});
      setLoadingState(false);
   }

   return (
      <>
         <Description>{searchType === SEARCH_TYPE.ALERT ? 'Stream name' : 'Stream name | Tag'}</Description>
         <FlexRow>
            <StaticCol width={150}>
               <Input
                  value={inputValue}
                  placeholder={'Insert stream name'}
                  onChange={(event) => setInputValue(event.target.value)}
                  onKeyDown={handleKeyDown}
               />
            </StaticCol>
            <StaticCol>
               <Button className='ml-2' color='primary' onClick={handleSearchClick} disabled={inputValue === ''}>
                  {isLoading ? (
                     <BlockSpinner
                        ballSizePx={10}
                        ballColor={'rgb(230, 230, 230)'}
                        paddingTopBottomPx={0}
                        widthPx={41.15}
                     />
                  ) : (
                     <>{'Search'}</>
                  )}
               </Button>
            </StaticCol>
         </FlexRow>
         {showInputNotification ? (
            <Notification>{`${issueMsg ? issueMsg : errorMessage}`}</Notification>
         ) : (
            <>
               {syncNote && <Notification $color='rgba(0, 0, 0, 0.75)' topMarginPx={7}>{`${syncNote}`}</Notification>}
               {showStreamSelection && (
                  <StreamSelection
                     streamIsDetermined={Object.keys(streamsData).length === 1}
                     isStreamSelectionOpen={isStreamSelectionOpen}
                     setOpenState={setOpenState}
                     selectedStream={selectedStream}
                     userData={Object.keys(streamsData).map((stream) => streamsData[stream])}
                     setStream={(streamName) =>
                        dispatch(
                           setTroubleshootingIngestStream(
                              getStreamPayload(streamName),
                              getStreamPayloadToUrlPush(streamName),
                           ),
                        )
                     }
                  />
               )}
               {selectedStream !== '' && (
                  <>
                     {showStreamNotification ? (
                        <Notification>{info}</Notification>
                     ) : searchType === SEARCH_TYPE.ALERT ? (
                        <AlertsTable streamName={selectedStream} created={created} />
                     ) : (
                        <PublishesTableContainer created={created} />
                     )}
                  </>
               )}
            </>
         )}

         {componenthasBeenMounted.current && ingestInput !== '' && !resetIsInProgress && (
            <IngestService
               ingestInput={ingestInputObj}
               url={url}
               orgaHash={orgaHash}
               cancelToken={cancelToken}
               onResult={handleStreamNameResult}
            />
         )}
      </>
   );
}

function getStreamPayload(streamName) {
   return {
      troubleIngestStream: { ingestStream: streamName },
      troubleSelectedPublish: {
         publishStart: '',
         publishEnd: '',
         publishFineStart: '',
         publishFineEnd: '',
      },
   };
}

function getStreamPayloadToUrlPush(streamName) {
   return {
      [URL_PARAMS.TROUBLE_INGEST_STREAM]: streamName,
      [URL_PARAMS.TROUBLE_INGEST_PUBLISH_START]: '',
      [URL_PARAMS.TROUBLE_INGEST_PUBLISH_END]: '',
      [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_START]: '',
      [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_END]: '',
   };
}

StreamSearch.propTypes = {
   searchType: PropTypes.string,
};

export default StreamSearch;
